import React, { useState } from 'react';
import styled from 'styled-components';
import { createUser } from '../services/apiService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 400px;
  color: ${props => props.theme.colors.text};
`;

const ModalTitle = styled.h2`
  margin: 0 0 30px 0;
  text-align: center;
  color: ${props => props.theme.colors.text};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.colors.inputBackground || props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#007bff'};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreateButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary || '#007bff'};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primaryDark || '#0056b3'};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.borderColor};
  color: ${props => props.theme.colors.text};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.borderColor};
    opacity: 0.8;
  }
`;

const ErrorMessage = styled.div`
  background-color: #6a1b1b;
  border: 1px solid #8b2e2e;
  color: #ff8b8b;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('username is required');
      return;
    }

    setLoading(true);
    try {
      await createUser(username);
      setUsername('');
      onUserCreated();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Create New User</ModalTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <form onSubmit={handleSubmit}>

          <FormGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="john_doe"
              disabled={loading}
            />
          </FormGroup>

          <ButtonGroup>
            <CreateButton
              type="submit"
              disabled={loading || !username.trim()}
            >
              {loading ? '...' : 'Create User'}
            </CreateButton>
            <CancelButton
              type="button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </CancelButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateUserModal;
